import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ScheduleItem,
  type ScheduleAlert,
  buildScheduleAlerts,
} from '@/lib/scheduleUtils';

interface Props {
  items: ScheduleItem[];
  computedRisk: Map<string, { delay_status: ScheduleItem['delay_status']; schedule_risk_level: ScheduleItem['schedule_risk_level']; delayDays: number }>;
  criticalItemIds: Set<string>;
  healthSummary: { completionImpactDays: number; overallHealth: string } | null;
  warning: string | null;
  viewerRole?: 'management' | 'subcontractor' | 'client';
  companyName?: string | null;
}

export default function ScheduleAlertsPanel({
  items,
  computedRisk,
  criticalItemIds,
  healthSummary,
  warning,
  viewerRole = 'management',
  companyName,
}: Props) {
  const { t } = useTranslation();
  const [showAlerts, setShowAlerts] = useState(true);
  const [alertFilter, setAlertFilter] = useState<string>('all');

  // Memoize alerts so they only rebuild when inputs change
  const alerts = useMemo(
    () =>
      buildScheduleAlerts(
        items,
        computedRisk,
        criticalItemIds,
        healthSummary?.completionImpactDays ?? 0,
        warning
      ),
    [items, computedRisk, criticalItemIds, healthSummary, warning]
  );

  // Subcontractor: only show alerts for their assigned items + project behind
  const visibleAlerts = useMemo(() => {
    if (viewerRole !== 'subcontractor' || !companyName) return alerts;
    return alerts.filter((a) => {
      if (a.type === 'project_behind') return true;
      if (!a.itemId) return false;
      const item = items.find((i) => i.id === a.itemId);
      if (!item) return false;
      const name = companyName.toLowerCase();
      return (
        item.assigned_to?.toLowerCase().includes(name) ||
        item.assigned_role?.toLowerCase().includes(name)
      );
    });
  }, [alerts, viewerRole, companyName, items]);

  // Client: simplified status only
  const clientAlerts = useMemo(() => {
    if (viewerRole !== 'client') return visibleAlerts;
    return visibleAlerts.filter((a) => a.type === 'project_behind' || a.type === 'at_risk');
  }, [visibleAlerts, viewerRole]);

  const displayAlerts = viewerRole === 'client' ? clientAlerts : visibleAlerts;

  if (displayAlerts.length === 0) return null;

  const filtered =
    alertFilter === 'all'
      ? displayAlerts
      : displayAlerts.filter((a) => a.type === alertFilter);

  const typeIcon: Record<string, string> = {
    critical_delayed: 'ri-error-warning-line text-[#EF4444]',
    at_risk: 'ri-alert-line text-[#F59E0B]',
    project_behind: 'ri-time-line text-[#7C3AED]',
    critical_path_warning: 'ri-error-warning-line text-[#EF4444]',
  };
  const typeBg: Record<string, string> = {
    critical_delayed: 'bg-[#FEF2F2] border-[#FECACA]',
    at_risk: 'bg-[#FEF0E7] border-[#FDE68A]',
    project_behind: 'bg-[#F5F3FF] border-[#DDD6FE]',
    critical_path_warning: 'bg-[#FEF2F2] border-[#FECACA]',
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <button
        onClick={() => setShowAlerts((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <i className="ri-notification-3-line text-[#EF4444]"></i>
          <span className="text-sm font-bold text-[#1A1A1A]">
            {displayAlerts.length > 1
              ? t('alertsPanel.alertCountPlural', { count: displayAlerts.length })
              : t('alertsPanel.alertCount', { count: displayAlerts.length })}
          </span>
        </div>
        <i
          className={`${showAlerts ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF] text-lg`}
        ></i>
      </button>
      {showAlerts && (
        <div className="px-4 pb-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {(['all', 'critical_delayed', 'at_risk', 'project_behind'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setAlertFilter(f)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold cursor-pointer whitespace-nowrap transition-colors ${
                  alertFilter === f ? 'bg-[#1A1A1A] text-white' : 'bg-gray-100 text-[#6B7280]'
                }`}
              >
                {f === 'all' ? t('alertsPanel.filterAll') : f === 'critical_delayed' ? t('alertsPanel.filterCriticalDelayed') : f === 'at_risk' ? t('alertsPanel.filterAtRisk') : t('alertsPanel.filterBehind')}
              </button>
            ))}
          </div>
          {filtered.slice(0, 6).map((alert, idx) => (
            <div key={idx} className={`flex items-start gap-2 rounded-xl p-2.5 border ${typeBg[alert.type]}`}>
              <i className={`${typeIcon[alert.type]} text-sm flex-shrink-0 mt-0.5`}></i>
              <div>
                <p className="text-xs font-bold text-[#1A1A1A]">{alert.title}</p>
                <p className="text-xs text-[#6B7280] leading-relaxed">{alert.message}</p>
              </div>
            </div>
          ))}
          {filtered.length > 6 && (
            <p className="text-[10px] text-[#9CA3AF] text-center">
              {t('alertsPanel.moreHidden', { count: filtered.length - 6 })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
